import { LinearProgress, Step, StepLabel, Stepper } from '@mui/material';
import { map } from 'lodash-es';
import { type FC, Fragment } from 'react';
import type { QuoteFlowSection } from 'types/QuoteFlow';
import {
  QuoteStepperLinearProgressStyle,
  QuoteStepperMainStepStyle,
  QuoteStepperStyle,
} from './QuoteStepper.styles';
import type { QuoteStepperProps } from './QuoteStepper.types';

const QuoteStepper: FC<QuoteStepperProps> = ({ sections, currentSection, currentStep }) => {
  /**
   * Ensure `currentSection` prop is within the valid range
   *
   * Prevent providing a negative value or a larger value than the `sections.length`
   */
  const validCurrentSectionIndex = Math.max(0, Math.min(currentSection, sections.length));

  return (
    <Stepper alternativeLabel sx={QuoteStepperStyle} connector={null}>
      {map(sections, (section: QuoteFlowSection, idx: number) => {
        const isActive = validCurrentSectionIndex === idx;
        const isSectionCompleted = validCurrentSectionIndex > idx;
        const isSectionDisabled = validCurrentSectionIndex < idx;

        let progressValue = 0;

        if (isSectionCompleted) {
          progressValue = 100;
        } else if (isActive) {
          /**
           * Section steps could be empty, set default length to 1
           *
           * This variable is using as divisor,
           * default 1 is for preventing devide by 0 error
           * and making `progressValue` calculation correct
           */
          const sectionStepsLength = section.steps?.length || 1;

          /**
           * Ensure `currentStep` prop is within the valid range
           *
           * Prevent providing a negative value or a larger value than the `sectionStepsLength`
           *
           * This validation also necessary for preventing `progressValue` calculation from going above 100,
           * it creates a styling problem in the `LinearProgress`, which is seen as not filled
           */
          const validCurrentStepIndex = Math.max(0, Math.min(currentStep, sectionStepsLength));

          progressValue = (validCurrentStepIndex / sectionStepsLength) * 100;
        }

        return (
          <Fragment key={`step-fragment-${section.key}`}>
            <Step
              key={`step-${section.key}`}
              index={idx}
              active={isActive}
              completed={isSectionCompleted}
              disabled={isSectionDisabled}
              sx={QuoteStepperMainStepStyle}
            >
              <StepLabel>{section.label}</StepLabel>
            </Step>

            {/* Don't show a progress bar for the last section */}
            {idx !== sections.length - 1 && (
              <Step
                key={`progressBar-${section.key}`}
                sx={{
                  flex: idx === 0 ? 3 : 1,
                  paddingInline: '0',
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={QuoteStepperLinearProgressStyle}
                />
              </Step>
            )}
          </Fragment>
        );
      })}
    </Stepper>
  );
};

export default QuoteStepper;
